import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import {
  FiCheck,
  FiLoader,
  FiAlertCircle,
  FiCheckCircle,
  FiHome,
  FiChevronRight,
  FiDollarSign,
} from "react-icons/fi";

import {
  getProfile,
  getDashboardSummary,
} from "../../store/slices/profile";

import {
  activateMyAccountFromWallet,
} from "../../store/slices/auth";

const SelfInvestment = () => {
  const dispatch = useDispatch();

  const fileInputRef = useRef(null);

  /* =====================================================
     PROFILE STATE
  ===================================================== */

  const {
    profilelist = {},
    loading = false,

    dashboardSummary,
    getDashboardSummaryLoading,
  } = useSelector((state) => state.profile);

  /* =====================================================
     AUTH STATE
  ===================================================== */

  const {
    activateAccountLoading = false,
  } = useSelector(
    (state) => state.auth || {}
  );

  /* =====================================================
     LOCAL STATE
  ===================================================== */

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [paymentSubmitted, setPaymentSubmitted] =
    useState(false);

  /* =====================================================
     FIXED INVESTMENT AMOUNT
  ===================================================== */

  const amount = 32;

  /* =====================================================
     GET PROFILE + DASHBOARD SUMMARY
  ===================================================== */

  useEffect(() => {
    dispatch(getProfile());

    dispatch(getDashboardSummary());
  }, [dispatch]);

  /* =====================================================
     PROFILE DATA
  ===================================================== */

  const profileData =
    profilelist?.data ||
    profilelist ||
    {};

  /* =====================================================
     CURRENT WALLET BALANCE

     SAME SOURCE AS WITHDRAW COMPONENT

     dashboardSummary.currentBalance
  ===================================================== */

  const currentBalance =
    dashboardSummary?.currentBalance;

  const walletBalance = Number(
    currentBalance || 0
  );

  /* =====================================================
     JOINING STATUS
  ===================================================== */

  const joiningStatus =
    profileData?.joiningStatus ||
    "";

  const normalizedStatus =
    String(joiningStatus).toUpperCase();

  const isPending =
    normalizedStatus === "PENDING";

  const isPaid =
    normalizedStatus === "PAID";

  const isSuccess =
    normalizedStatus === "SUCCESS";

  const isRejected =
    normalizedStatus === "REJECTED";

  /* =====================================================
     HANDLE SUBMIT
  ===================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    /* =================================================
       GET CURRENT BALANCE

       SAME LOGIC AS WITHDRAW
    ================================================= */

    const availableBalance = Number(
      dashboardSummary?.currentBalance || 0
    );

    console.log(
      "CURRENT WALLET BALANCE:",
      availableBalance
    );

    console.log(
      "REQUIRED INVESTMENT AMOUNT:",
      amount
    );

    /* =================================================
       WALLET BALANCE VALIDATION
    ================================================= */

    if (availableBalance < amount) {
      setError(
        `Insufficient wallet balance. Your current balance is ${availableBalance.toFixed(
          2
        )} USDT. You need ${amount} USDT.`
      );

      return;
    }

    /* =================================================
       SUBMIT
    ================================================= */

    setSubmitting(true);

    try {
      const payload = {
        amount,
      };

      console.log(
        "ACTIVATE ACCOUNT FROM WALLET PAYLOAD:",
        payload
      );

      const response =
        await dispatch(
          activateMyAccountFromWallet(payload)
        ).unwrap();

      console.log(
        "ACTIVATE ACCOUNT RESPONSE:",
        response
      );

      /* =================================================
         SUCCESS
      ================================================= */

      setSuccess(
        response?.message ||
        "Account activated successfully from wallet."
      );

      setPaymentSubmitted(true);

      /* Clear file input */

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      /* =================================================
         REFRESH PROFILE
      ================================================= */

      dispatch(getProfile());

      /* =================================================
         REFRESH WALLET BALANCE

         SAME API USED BY WITHDRAW
      ================================================= */

      dispatch(getDashboardSummary());

    } catch (err) {
      console.error(
        "ACTIVATE ACCOUNT ERROR:",
        err
      );

      const apiError =
        typeof err === "string"
          ? err
          : err?.message ||
          err?.response?.data?.message ||
          err?.response?.data?.error?.message ||
          err?.response?.data?.error ||
          "Failed to activate account from wallet.";

      setError(apiError);

    } finally {
      setSubmitting(false);
    }
  };

  /* =====================================================
     STATUS CONTENT
  ===================================================== */

  const renderStatusContent = () => {

    /* =================================================
       ACTIVATION SUCCESS
    ================================================= */

    if (paymentSubmitted) {
      return (
        <div className="gi-payment-status-content">

          <FiCheckCircle />

          <h3>
            Account Activated Successfully
          </h3>

          <p>
            Your account has been successfully
            activated using your wallet balance.
          </p>

        </div>
      );
    }

    /* =================================================
       PAID / SUCCESS
    ================================================= */

    if (isPaid || isSuccess) {
      return (
        <div className="gi-payment-status-content success">

          <FiCheckCircle />

          <h3>
            Payment Successful
          </h3>

          <p>
            Your joining payment has been
            successfully approved.
          </p>

        </div>
      );
    }

    /* =================================================
       REJECTED
    ================================================= */

    if (isRejected) {
      return (
        <div className="gi-payment-status-content rejected">

          <FiAlertCircle />

          <h3>
            Payment Rejected
          </h3>

          <p>
            Your payment request was rejected.
            Please try again.
          </p>

        </div>
      );
    }

    /* =================================================
       DEFAULT
    ================================================= */

    return (
      <div className="gi-payment-status-content">

        <FiAlertCircle />

        <h3>
          Payment Status
        </h3>

        <p>
          Current status:
          {" "}

          <strong>
            {joiningStatus || "-"}
          </strong>
        </p>

      </div>
    );
  };

  /* =====================================================
     LOADING
  ===================================================== */

  if (
    loading &&
    !profileData?._id
  ) {
    return (
      <div className="gi-payment-page">

        <div className="gi-payment-loading">

          <FiLoader />

          <span>
            Loading payment details...
          </span>

        </div>

      </div>
    );
  }

  /* =====================================================
     UI
  ===================================================== */

  return (
    <>
      <div className="gi-transfer-page">

        {/* =================================================
            BREADCRUMB
        ================================================= */}

        <div className="gi-breadcrumb">

          <Link to="/dashboard">

            <FiHome />

            Dashboard

          </Link>

          <FiChevronRight
            className="gi-breadcrumb-arrow"
          />

          <span>
            Self Investment
          </span>

        </div>

        {/* =================================================
            PAYMENT CARD
        ================================================= */}

        <div className="gi-payment-card">

          {/* =================================================
              SUCCESS
          ================================================= */}

          {success && (
            <div className="gi-payment-success">

              <FiCheck />

              <span>
                {success}
              </span>

            </div>
          )}

          {/* =================================================
              ERROR
          ================================================= */}

          {error && (
            <div className="gi-payment-error">

              <FiAlertCircle />

              <span>
                {error}
              </span>

            </div>
          )}

          {/* =================================================
              FORM
          ================================================= */}

          {isPending && !paymentSubmitted ? (

            <form
              onSubmit={handleSubmit}
            >

              {/* =================================================
                  CURRENT WALLET BALANCE
              ================================================= */}

              <div className="gi-payment-form-group">

                <label>
                  Current Wallet Balance
                </label>

                <div
                  style={{
                    position: "relative",
                  }}
                >

                  <FiDollarSign
                    style={{
                      position: "absolute",
                      left: "14px",
                      top: "50%",
                      transform:
                        "translateY(-50%)",
                    }}
                  />

                  <input
                    type="text"
                    value={
                      getDashboardSummaryLoading
                        ? "Loading..."
                        : `${walletBalance.toFixed(
                          2
                        )} USDT`
                    }
                    readOnly
                    className="gi-payment-input"
                    style={{
                      paddingLeft: "40px",
                    }}
                  />

                </div>

              </div>

              {/* =================================================
                  INVESTMENT AMOUNT
              ================================================= */}

              <div className="gi-payment-form-group">

                <label>
                  Amount (USDT BEP20)
                </label>

                <input
                  type="text"
                  value={amount}
                  readOnly
                  className="gi-payment-input"
                />

              </div>

              {/* =================================================
                  INSUFFICIENT BALANCE
              ================================================= */}

              {!getDashboardSummaryLoading &&
                walletBalance < amount && (

                  <div className="gi-payment-error">

                    <FiAlertCircle />

                    <span>
                      Insufficient wallet balance.
                      You need{" "}
                      <strong>
                        {amount} USDT
                      </strong>
                      {" "}but your current balance is{" "}
                      <strong>
                        {walletBalance.toFixed(2)} USDT
                      </strong>.
                    </span>

                  </div>
                )}

              {/* =================================================
                  SUBMIT
              ================================================= */}

              <button
                type="submit"
                className="gi-payment-submit-btn"
                disabled={
                  uploading ||
                  submitting ||
                  activateAccountLoading ||
                  getDashboardSummaryLoading ||
                  walletBalance < amount
                }
              >

                {submitting ||
                  activateAccountLoading ? (

                  <>
                    <FiLoader />

                    Activating...

                  </>

                ) : (

                  "Activate Account From Wallet"

                )}

              </button>

            </form>

          ) : (

            renderStatusContent()

          )}

        </div>

      </div>
    </>
  );
};

export default SelfInvestment;